/**
 * 独享容器详情：网络 / 应用 / 配置 标签页。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Button, Card, Collapse, Descriptions, Empty, Form, Input, InputNumber, Popconfirm, Radio, Select, Slider,
    Space, Spin, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation, t as tr } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { containerUserApi as api, isOk, errMsg } from '../../../shared/services/containerService';
import { PortTable, specText, arr } from '../../../shared/components/container/ContainerParts';
import { useFilesSocket } from '../../../shared/components/container/filesSocket';
import { SocketBar, ProcessList, LiveLogs } from '../../../shared/components/container/ContainerTools';
import { CronCard, ServicesCard, DeployCard, PhpCard } from './AppTools';
import EgressCard from '../../../shared/components/container/EgressCard';

const { Text } = Typography;
export const money = (v, cur) => formatMoney(Number(v || 0), cur || PLATFORM_CURRENCY);

const useAddons = (id, gb) => {
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        const r = await api.addons(id, gb);
        if (isOk(r)) setData(r.data || {});
        else { setData((d) => d || {}); message.error(errMsg(r, tr('Failed to load'))); }
    }, [id, gb]);
    useEffect(() => { load(); }, [load]);
    return [data, load];
};

const useGoInvoice = () => {
    const navigate = useNavigate();
    return (iid) => navigate(userPath(`/billing/invoices/${iid}`));
};

// ------------------------------------------------------------------ 网络

export const NetworkTab = ({ c, reload }) => {
    const { t } = useTranslation();
    const goInvoice = useGoInvoice();
    const net = c.network || {};
    const [form] = Form.useForm();
    const [addons, loadAddons] = useAddons(c.id);
    const [adding, setAdding] = useState(false);
    const [groups, setGroups] = useState(null);
    const [sgIds, setSgIds] = useState([]);
    const [editingSg, setEditingSg] = useState(false);
    const [savingSg, setSavingSg] = useState(false);
    const [buying, setBuying] = useState(false);
    const bound = arr(net.security_groups);
    const ports = addons?.ports || {};
    const range = arr(ports.range);
    const full = ports.max !== undefined && Number(ports.used) >= Number(ports.max);
    const eipQ = addons?.eip || {};
    const eips = arr(net.eips).length ? arr(net.eips) : arr(addons?.eips);

    const after = () => { reload(); loadAddons(); };
    const addPort = async (v) => {
        setAdding(true);
        const body = { protocol: v.protocol, internal_port: v.internal_port };
        if (v.external_port) body.external_port = v.external_port;
        const r = await api.addPort(c.id, body);
        setAdding(false);
        if (isOk(r)) { message.success(t('Added')); form.resetFields(['internal_port', 'external_port']); after(); } else message.error(errMsg(r, t('Failed to add')));
    };
    const delPort = async (p) => {
        const r = await api.deletePort(c.id, p.id);
        if (isOk(r)) { message.success(t('Deleted')); after(); } else message.error(errMsg(r, t('Deletion failed')));
    };
    const editSg = async () => {
        setSgIds(bound.map((g) => g.id));
        setEditingSg(true);
        if (groups === null) {
            const r = await api.securityGroupList();
            setGroups(isOk(r) ? arr(r.data?.security_groups) : []);
        }
    };
    const saveSg = async () => {
        setSavingSg(true);
        const r = await api.setSecurityGroups(c.id, sgIds);
        setSavingSg(false);
        if (isOk(r)) { message.success(t('Saved')); setEditingSg(false); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const buyEip = async () => {
        setBuying(true);
        const r = await api.buyEip(c.id);
        setBuying(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Purchase failed'))); return; }
        const d = r.data || {};
        if (!d.paid && d.invoice_id) { goInvoice(d.invoice_id); return; }
        message.success(t('Purchased'));
        after();
    };
    const releaseEip = async (e) => {
        const r = await api.releaseEip(c.id, e.id);
        if (isOk(r)) { message.success(t('Released')); after(); } else message.error(errMsg(r, t('Release failed')));
    };

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={{ xs: 1, sm: 3 }} bordered>
                <Descriptions.Item label={t('Private IP')}>{net.private_ip || c.private_ip || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('Gateway')}>{net.gateway || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('Public IP')}>{net.public_ip || '-'}</Descriptions.Item>
            </Descriptions>
            <EgressCard egress={c.egress} onSave={(mode, fallback) => api.egress(c.id, { mode, fallback })} onSaved={reload} />
            <Card size="small" title={t('Port mapping')}
                extra={ports.max !== undefined && <Text type={full ? 'danger' : 'secondary'}>{ports.used ?? 0}/{ports.max}</Text>}>
                <Form form={form} layout="inline" onFinish={addPort} initialValues={{ protocol: 'tcp' }} style={{ marginBottom: 12, rowGap: 8 }}>
                    <Form.Item name="protocol"><Select style={{ width: 80 }} options={[{ value: 'tcp', label: 'TCP' }, { value: 'udp', label: 'UDP' }]} /></Form.Item>
                    <Form.Item name="internal_port" rules={[{ required: true, message: t('Enter the container port') }]}>
                        <InputNumber min={1} max={65535} placeholder={t('Container port')} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="external_port"
                        tooltip={range.length === 2 ? t('Optional {{min}}-{{max}}; leave empty to auto-assign', { min: range[0], max: range[1] }) : undefined}>
                        <InputNumber min={range[0] || 1} max={range[1] || 65535} placeholder={t('Public port (auto)')} style={{ width: 140 }} />
                    </Form.Item>
                    <Form.Item><Button htmlType="submit" icon={<PlusOutlined />} loading={adding} disabled={full}>{t('Add')}</Button></Form.Item>
                </Form>
                <PortTable ports={net.ports} publicIp={net.public_ip} onDelete={delPort} />
            </Card>
            <Card size="small" title={t('Dedicated public IP')}>
                {eips.length ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {eips.map((e) => (
                            <Space key={e.id}>
                                <Text copyable style={{ fontFamily: 'monospace' }}>{e.public_ip}</Text>
                                <Popconfirm title={t('Release this IP?')} okType="danger" onConfirm={() => releaseEip(e)}>
                                    <Button size="small" danger type="link">{t('Release')}</Button>
                                </Popconfirm>
                            </Space>
                        ))}
                    </Space>
                ) : !addons ? <Spin size="small" /> : (
                    <Space wrap>
                        {eipQ.ok === false ? <Text type="secondary">{eipQ.error || t('Currently unavailable for purchase')}</Text> : (
                            <Tooltip title={eipQ.days ? t('Billed for the remaining {{days}} days, {{price}}/month', { days: eipQ.days, price: money(eipQ.unit_price, eipQ.currency) }) : undefined}>
                                <Text strong>{money(eipQ.amount, eipQ.currency)}</Text>
                            </Tooltip>
                        )}
                        <Popconfirm title={t('Buy a dedicated public IP?')} disabled={eipQ.ok === false} onConfirm={buyEip}>
                            <Button type="primary" size="small" loading={buying} disabled={eipQ.ok === false}>{t('Buy')}</Button>
                        </Popconfirm>
                    </Space>
                )}
            </Card>
            <Card size="small" title={t('Security groups')} extra={!editingSg && <Button type="link" size="small" onClick={editSg}>{t('Edit')}</Button>}>
                {editingSg ? (
                    <Space wrap>
                        <Select mode="multiple" style={{ minWidth: 280 }} value={sgIds} onChange={setSgIds} loading={groups === null}
                            options={arr(groups).map((g) => ({ value: g.id, label: g.name }))} />
                        <Button type="primary" loading={savingSg} onClick={saveSg}>{t('Save')}</Button>
                        <Button onClick={() => setEditingSg(false)}>{t('Cancel')}</Button>
                    </Space>
                ) : (bound.length ? <Space wrap>{bound.map((g) => <Tag key={g.id}>{g.name}</Tag>)}</Space> : <Text type="secondary">{t('Not bound')}</Text>)}
            </Card>
        </Space>
    );
};

// ------------------------------------------------------------------ 应用

const KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const parseEnv = (list) => arr(list).map((s, i) => {
    const str = String(s);
    const k = str.indexOf('=');
    return { rid: i, key: k < 0 ? str : str.slice(0, k), value: k < 0 ? '' : str.slice(k + 1) };
});
const quoteArg = (a) => (/^[A-Za-z0-9_@%+=:,./-]+$/.test(a) ? a : `'${String(a).replace(/'/g, `'\\''`)}'`);
const cmdText = (cmd) => (Array.isArray(cmd) ? cmd.map(quoteArg).join(' ') : String(cmd || ''));

const EnvEditor = ({ c, reload }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState(() => parseEnv(c.env));
    const [saving, setSaving] = useState(false);
    const seq = useRef(1000);
    const set = (rid, k, v) => setRows((rs) => rs.map((r) => (r.rid === rid ? { ...r, [k]: v } : r)));
    const bad = rows.some((r) => !KEY_RE.test(r.key.trim()));
    const save = async () => {
        const env = {};
        rows.forEach((r) => { env[r.key.trim()] = r.value; });
        setSaving(true);
        const r = await api.config(c.id, { env });
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const cols = [
        { title: 'KEY', key: 'k', width: '35%', render: (_, r) => <Input size="small" status={KEY_RE.test(r.key.trim()) ? '' : 'error'} value={r.key} onChange={(e) => set(r.rid, 'key', e.target.value)} /> },
        { title: 'VALUE', key: 'v', render: (_, r) => <Input size="small" value={r.value} onChange={(e) => set(r.rid, 'value', e.target.value)} /> },
        { title: '', key: 'op', width: 48, render: (_, r) => <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => setRows((rs) => rs.filter((x) => x.rid !== r.rid))} /> },
    ];
    return (
        <div>
            <Table size="small" rowKey="rid" columns={cols} dataSource={rows} pagination={false} locale={{ emptyText: t('No environment variables') }} />
            <Space style={{ marginTop: 8 }} wrap>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => { seq.current += 1; setRows((rs) => [...rs, { rid: seq.current, key: '', value: '' }]); }}>{t('Add')}</Button>
                <Button size="small" type="primary" loading={saving} disabled={bad} onClick={save}>{t('Save')}</Button>
                <Text type="secondary" style={{ fontSize: 12 }}>{t('The container restarts after saving')}</Text>
            </Space>
        </div>
    );
};

const CmdEditor = ({ c, reload }) => {
    const { t } = useTranslation();
    const [cmd, setCmd] = useState(() => cmdText(c.cmd));
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        const r = await api.config(c.id, { cmd: cmd.trim() });
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };
    return (
        <Space.Compact style={{ width: '100%' }}>
            <Input value={cmd} onChange={(e) => setCmd(e.target.value)} placeholder={t('Leave empty to use the image default')} style={{ fontFamily: 'monospace' }} />
            <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
        </Space.Compact>
    );
};

export const AppTab = ({ c, reload }) => {
    const { t } = useTranslation();
    const fetchTicket = useCallback(() => api.files(c.id), [c.id]);
    const { sockRef, state, error, connect } = useFilesSocket(fetchTicket);
    const ready = state === 'open';
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title={t('Environment variables')}>
                <EnvEditor c={c} reload={reload} />
                <Collapse ghost size="small" style={{ marginTop: 8 }} items={[{
                    key: 'cmd', label: t('Start command'), children: <CmdEditor c={c} reload={reload} />,
                }]} />
            </Card>
            {c.php && <PhpCard c={c} reload={reload} />}
            <CronCard c={c} reload={reload} />
            <ServicesCard c={c} reload={reload} />
            <SocketBar state={state} error={error} onReconnect={connect} />
            <DeployCard c={c} sockRef={sockRef} ready={ready} />
            <Card size="small" title={t('Processes')}><ProcessList sockRef={sockRef} ready={ready} /></Card>
            <Card size="small" title={t('Logs')}><LiveLogs sockRef={sockRef} ready={ready} /></Card>
        </Space>
    );
};

// ------------------------------------------------------------------ 配置

const DiskCard = ({ c, reload }) => {
    const { t } = useTranslation();
    const goInvoice = useGoInvoice();
    const [gb, setGb] = useState(null);
    const [q, setQ] = useState(null);
    const [loading, setLoading] = useState(false);
    const [buying, setBuying] = useState(false);
    const timer = useRef(null);

    const quote = useCallback(async (n) => {
        setLoading(true);
        const r = await api.addons(c.id, n);
        setLoading(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Failed to load'))); return; }
        const d = r.data?.disk || {};
        setQ(d);
        const room = Math.max(0, (Number(d.max_gb) || 0) - (Number(d.current_gb) || 0));
        if (n == null) setGb(room ? Math.min(10, room) : 0);
        else if (n > room && room > 0) setGb(room);
    }, [c.id, t]);

    useEffect(() => { quote(null); }, [quote]);
    useEffect(() => {
        if (!gb) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => quote(gb), 350);
        return () => clearTimeout(timer.current);
    }, [gb, quote]);

    const buy = async () => {
        setBuying(true);
        const r = await api.buyDisk(c.id, gb);
        setBuying(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Purchase failed'))); return; }
        const d = r.data || {};
        if (d.invoice_id && !d.paid) { goInvoice(d.invoice_id); return; }
        message.success(t('Expanded'));
        reload();
        quote(null);
    };

    if (!q) return <Card size="small" title={t('Disk expansion')}><Spin size="small" /></Card>;
    const cur = Number(q.current_gb) || 0;
    const room = Math.max(0, (Number(q.max_gb) || 0) - cur);
    return (
        <Card size="small" title={t('Disk expansion')}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>{t('Online expansion, no restart; size can only grow')}</Text>
            {room < 1 ? <Text type="secondary">{q.error || t('Limit reached')}</Text> : (
                <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
                    <Space wrap>
                        <Text>{cur} GB +</Text>
                        <InputNumber min={1} max={room} value={gb} onChange={(v) => setGb(v || 1)} addonAfter="GB" style={{ width: 130 }} />
                        <Text type="secondary">= {cur + (gb || 0)} GB</Text>
                    </Space>
                    {room > 1 && <Slider min={1} max={room} value={gb || 1} onChange={setGb} />}
                    <Space wrap>
                        {q.ok === false ? <Text type="danger">{q.error}</Text> : (
                            <Tooltip title={q.days ? t('Billed for the remaining {{days}} days', { days: q.days }) : undefined}>
                                <Text strong>{loading ? <Spin size="small" /> : money(q.amount, q.currency)}</Text>
                            </Tooltip>
                        )}
                        <Popconfirm title={t('Add {{gb}} GB?', { gb })} disabled={q.ok === false || loading} onConfirm={buy}>
                            <Button type="primary" loading={buying} disabled={q.ok === false || loading}>{t('Expand capacity')}</Button>
                        </Popconfirm>
                    </Space>
                </Space>
            )}
        </Card>
    );
};

const PlanCard = ({ c, reload }) => {
    const { t } = useTranslation();
    const goInvoice = useGoInvoice();
    const [addons] = useAddons(c.id);
    const [pick, setPick] = useState(null);
    const [busy, setBusy] = useState(false);
    const plans = arr(addons?.plans);
    const sel = plans.find((p) => p.product_id === pick);

    const submit = async () => {
        setBusy(true);
        const r = await api.changePlan(c.id, pick);
        setBusy(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Operation failed'))); return; }
        const d = r.data || {};
        if (d.scheduled) { message.success(t('Scheduled; takes effect at renewal')); return; }
        if (d.invoice_id) { goInvoice(d.invoice_id); return; }
        message.success(t('Changed'));
        reload();
    };

    return (
        <Card size="small" title={t('Change plan')}>
            {!addons ? <Spin size="small" /> : !plans.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No plans to change to')} /> : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio.Group value={pick} onChange={(e) => setPick(e.target.value)} style={{ width: '100%' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {plans.map((p) => (
                                <Radio key={p.product_id} value={p.product_id} disabled={!!p.error}>
                                    <Space wrap size={8}>
                                        <Tag color={p.direction === 'upgrade' ? 'blue' : 'default'}>{p.direction === 'upgrade' ? t('Upgrade') : t('Downgrade')}</Tag>
                                        <Text strong>{p.name}</Text>
                                        <Text type="secondary">{specText({ ...(p.spec || {}), disk_gb: p.spec?.disk_gb_effective ?? p.spec?.disk_gb })}</Text>
                                        {p.cost != null && p.direction === 'upgrade' && <Text>{money(p.cost, p.currency)}</Text>}
                                        {p.error && <Text type="danger" style={{ fontSize: 12 }}>{p.error}</Text>}
                                    </Space>
                                </Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                    {sel && <Alert type="info" showIcon message={sel.direction === 'upgrade' ? t('Takes effect now; the difference is pro-rated') : t('Takes effect at renewal')} />}
                    <Popconfirm title={t('Change the plan?')} disabled={!sel} onConfirm={submit}>
                        <Button type="primary" disabled={!sel} loading={busy}>{t('Confirm change')}</Button>
                    </Popconfirm>
                </Space>
            )}
        </Card>
    );
};

export const ConfigTab = ({ c, reload }) => (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <DiskCard c={c} reload={reload} />
        <PlanCard c={c} reload={reload} />
    </Space>
);
